import { baseballLogicService, DataGridRowManager, ICellsSelection } from '@trackman/baseball-shared-components';
import dayjs from 'dayjs';
import { Ruleset } from 'enums/Ruleset';
import { SessionType } from 'enums/SessionType';
import { IApiSessionTeam } from 'interfaces/review/session/Session';
import { DataKey } from '../../enums/DataKey';
import { IApiSessionMetadataUpdated } from '../../interfaces/tagging/ApiModels/IApiSessionMetadataUpdated';
import { ReviewDataRow } from './ReviewDataRow/ReviewDataRow';
import { ReviewGame } from './ReviewGame';

export class SessionReviewRowManager extends DataGridRowManager<ReviewDataRow> {
  sessionMetadata: IApiSessionMetadataUpdated = {} as IApiSessionMetadataUpdated;

  private isBattingPractice = false;

  private isScrimmage = false;

  isDisplayingWarmups = true;

  onGridUpdate?: () => void;

  parseRows(reviewGame: ReviewGame): void {
    if (!reviewGame.plays) {
      return;
    }
    const { homeTeam } = reviewGame.sessionMetadata;
    const { awayTeam } = reviewGame.sessionMetadata;
    const mappedRows = reviewGame.plays.map((play) => new ReviewDataRow(play, homeTeam?.name ?? '', awayTeam?.name ?? ''));

    this.sessionMetadata = reviewGame.sessionMetadata;
    this.isBattingPractice = reviewGame.sessionMetadata.sessionType === SessionType.BattingPractice;
    this.isScrimmage = reviewGame.sessionMetadata.ruleSet === Ruleset.scrimmage;
    this.setRows(mappedRows);
    this.recalculateComputedProperties();
  }

  insertRowAtPosition(row: ReviewDataRow, rowId: string, insertBelow = false) {
    row.isChanged = true;
    const indexManipulation = insertBelow ? 1 : 0;
    this.rows.splice(this.findIndex(rowId) + indexManipulation, 0, row);
    this._updateSelection(undefined);
    this.validateRows();
    this.recalculateDisplayIndex();
    this.cellCacheManager.rerenderAllCells();
  }

  deleteRows(fromRowId: string, numRows: number) {
    const fromIndex = this.findIndex(fromRowId);
    for (let i = fromIndex; i < fromIndex + numRows; i++) {
      this.rows[i].isDeleted = true;
      this.rows[i].isChanged = true;
    }
    this.validateRows();
    this.recalculateDisplayIndex();
    this.cellCacheManager.rerenderAllCells();
  }

  recalculateDisplayIndex() {
    let i = 0;
    this.rows.forEach((row, idx) => {
      let dispIdx = row.displayIndex;
      if (row.isDeleted || (!this.isDisplayingWarmups && row.isWarmup)) {
        dispIdx = -1;
      } else {
        dispIdx = i;
        i++;
      }
      if (row.displayIndex !== dispIdx) {
        row.displayIndex = dispIdx;
        this.cellCacheManager.addRowsToUpdate([idx]);
      }
    });
    this.setLivePlays();
  }

  recalculateComputedProperties() {
    const fixedApiPlays = baseballLogicService.recalculateApiPlayComputedProperties(
      this.rows.map((r) => r.toApiPlay(this.sessionMetadata).play)
    );
    for (let i = 0; i < this.rows.length; i++) {
      const play = this.rows[i];
      const { taggerBehavior } = fixedApiPlays[i];
      if (
        !this.isBattingPractice &&
        (taggerBehavior.pAofInning != play.pAofInning || // Usage of != instead of !== is by design
          taggerBehavior.pitchOfPA != play.pitchOfPA)
      ) {
        play.pAofInning = taggerBehavior.pAofInning;
        play.pitchOfPA = taggerBehavior.pitchOfPA;
        play.isChanged = true;
        this.cellCacheManager.addRowsToUpdate([i]);
      }
      if (
        taggerBehavior.playSequenceNumber != play.playSequenceNumber || // Usage of != instead of !== is by design
        taggerBehavior.sequenceNumber != play.sequenceNumber
      ) {
        play.playSequenceNumber = taggerBehavior.playSequenceNumber;
        play.sequenceNumber = taggerBehavior.sequenceNumber;
        play.isChanged = true;
        this.cellCacheManager.addRowsToUpdate([i]);
      }
    }
  }

  private timestampExist = (row: ReviewDataRow) => {
    return row.trackStartTimeLocal !== null && row.trackStartTimeLocal !== undefined && row.trackStartTimeLocal !== '';
  };

  private getPreviousRowWithTimestamp = (rows: ReviewDataRow[], currentIndex: number) => {
    for (let i = currentIndex - 1; i >= 0; i--) {
      const row = rows[i];
      if (this.timestampExist(row)) {
        return { row, missingTimestamps: i };
      }
    }
  };

  private getNextRowWithTimestamp = (rows: ReviewDataRow[], currentIndex: number) => {
    for (let i = currentIndex + 1; i < rows.length; i++) {
      const row = rows[i];
      if (this.timestampExist(row)) {
        return { row, missingTimestamps: i };
      }
    }
  };

  autogenerateMissingTimestamps = (rows: ReviewDataRow[]) => {
    rows.forEach((row, index) => {
      if (row.isWarmup || row.isDeleted) {
        return;
      }

      if (!this.timestampExist(row)) {
        const prevRow = this.getPreviousRowWithTimestamp(rows, index);
        const nextRow = this.getNextRowWithTimestamp(rows, index);

        if (prevRow && nextRow) {
          const startDiff = dayjs(nextRow.row.trackStartTimeLocal).diff(dayjs(prevRow.row.trackStartTimeLocal));
          const missingCount = nextRow.missingTimestamps - prevRow.missingTimestamps;
          const indexOfNextRowWithTimestamp = nextRow.missingTimestamps;

          for (let i = index, j = 1; i < indexOfNextRowWithTimestamp; i++, j++) {
            const row = rows[i];

            if (row.isWarmup || row.isDeleted) {
              continue;
            }

            const fillValue = dayjs(prevRow.row.trackStartTimeLocal).add((startDiff / missingCount) * j);

            if (row) {
              row.trackStartTimeLocal = fillValue.format();
              row.trackStartTimeAutoGenerated = true;
              row.isChanged = true;
            }
          }
        }

        if (prevRow && !nextRow) {
          for (let i = index; i < rows.length; i++) {
            const row = rows[i];

            if (row.isWarmup || row.isDeleted) {
              continue;
            }

            if (row) {
              const fillValue = dayjs(prevRow.row.trackStartTimeLocal).add(i - index + 1, 'second');

              row.trackStartTimeLocal = fillValue.format();
              row.trackStartTimeAutoGenerated = true;
              row.isChanged = true;
            }
          }
        }

        if (!prevRow && nextRow) {
          const missingCount = nextRow.missingTimestamps;

          for (let i = index, j = missingCount; j > 0; i++, j--) {
            const row = rows[i];

            if (row.isWarmup || row.isDeleted) {
              continue;
            }

            if (row) {
              const fillValue = dayjs(nextRow.row.trackStartTimeLocal).subtract(j, 'second');

              row.trackStartTimeLocal = fillValue.format();
              row.trackStartTimeAutoGenerated = true;
              row.isChanged = true;
            }
          }
        }
      }
    });
  };

  updateRow(index: number, dataKey: string, newValue: any) {
    const oldValue = this.rows[index].getValue(dataKey);

    super.updateRow(index, dataKey, newValue, '');
    this.recalculateComputedProperties();
    this.validateRows();
    if (dataKey === DataKey.IsWarmup) {
      this.recalculateDisplayIndex();
      this.cellCacheManager.rerenderAllCells();
      this.updateGrid();
    }
    this.cellCacheManager.addRowsToUpdate([index, index + 1]);
  }

  selectionChangeMiddleware = (_oldSelection: ICellsSelection | undefined, newSelection: ICellsSelection | undefined) => {
    if (newSelection) {
      const { isError, message } = this.getSelectionError(newSelection);
      newSelection.isSelectionError = isError;
      newSelection.selectionErrorText = message;
    }
    return newSelection;
  };

  getSelectionError(selection: ICellsSelection) {
    const selectedRows = this.rows.slice(selection.fromRowIndex, selection.toRowIndex + 1);
    const innings = selectedRows.filter((r) => !r.isDeleted).map((r) => `${r.inning.inningNumber}-${r.inning.isTopInning}`);
    if (new Set(innings).size > 1) {
      return {
        isError: true,
        message: 'You cannot select across multiple innings',
      };
    }
    return { isError: false, message: '' };
  }

  setIsDisplayingWarmups(newVal: boolean) {
    if (newVal !== this.isDisplayingWarmups) {
      this.isDisplayingWarmups = newVal;
      this._updateSelection(undefined);
      this.recalculateDisplayIndex();
      this.cellCacheManager.rerenderAllCells();
      this.updateGrid();
    }
  }

  updateGrid() {
    super.updateGrid();
    if (this.onGridUpdate) {
      this.onGridUpdate();
    }
  }

  handleTeamsChanged(newHomeTeam: IApiSessionTeam, newAwayTeam: IApiSessionTeam, isHomeTeamChanged: boolean, isAwayTeamChanged: boolean) {
    for (let i = 0; i < this.rows.length; i++) {
      const row = this.rows[i];
      if (row.isDeleted) {
        continue;
      }
      if (isHomeTeamChanged) {
        row.changeTeam(newHomeTeam.name, true);
      }
      if (isAwayTeamChanged) {
        row.changeTeam(newAwayTeam.name, false);
      }
    }
    this.recalculateComputedProperties();
    this.validateRows();
    this.cellCacheManager.rerenderAllCells();
    this.updateGrid();
  }
}
